import React, { useState, useMemo, useEffect } from 'react'
import { useHistory } from 'react-router'
import { Spinner } from '../../Loaders/Loaders'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import Revert from '@tecuity/sos-icons/react/revert'
import '../../Settings/SettingsViews/PropertiesList.scss'
import './PayorAccountDetails.scss'
import { setErrorToast, setSuccessToast } from '../../../actions'
import * as api from '../../../requests'
import { Checkbox } from '@tecuity/granite-ui'
import LinkedUserAccounts from './LinkedUserAccounts'
import { getStateOptions, getCountryOptions, getUSADefault } from '../../Wizard2/selectOptions'
import { useDispatch } from 'react-redux'
import { isCountryUSA } from '../../../Utilities/Utilities'

const PayorAccountDetails = ({ isAdding, accountId, onSave, showBillableFlag }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [error, setError] = useState('')
  const [payorAccount, setPayorAccount] = useState(defaultPayorAccount)

  const resetPayorAccount = () => {
    if (accountId) {
      setLoading(true)
      api.GET_FREQUENT_PAYOR_BY_ID(
        accountId,
        (response) => {
          setPayorAccount(response)
          setLoading(false)
        },
        (err) => {
          setLoading(false)
          setError(err)
          console.error(err)
        }
      )
    } else {
      setPayorAccount({ ...defaultPayorAccount })
    }
  }

  useEffect(() => {
    resetPayorAccount()
  }, [accountId])

  const ContactAddress = useMemo(() => {
    return payorAccount.ContactAddress
  }, [payorAccount])

  const isUSA = useMemo(() => {
    const { Country } = ContactAddress
    return isCountryUSA(Country)
  }, [ContactAddress])

  const updatePayorValue = (accessor, val) => {
    console.log(accessor, val)
    setIsDirty(true)
    setPayorAccount((info) => ({ ...info, [accessor]: val }))
  }

  const updatePayorAddressValue = (accessor, val) => {
    setIsDirty(true)
    setPayorAccount((info) => ({
      ...info,
      ContactAddress: {
        ...info.ContactAddress,
        [accessor]: val,
      },
    }))
  }

  const handleSave = () => {
    setLoading(true)
    api.SAVE_FREQUENT_PAYOR(
      accountId > 0,
      payorAccount,
      (response) => {
        setPayorAccount(response)
        setLoading(false)
        dispatch(setSuccessToast(`Account #${response.FrequentPayorNum} Saved Successfully.`))
        onSave()
      },
      handleFailedSave
    )
  }

  const handleFailedSave = (err) => {
    if (err.DisplayToUser === true && (err.Message || '') !== '') {
      dispatch(setErrorToast(err.Message))
    } else {
      dispatch(setErrorToast('Failed to save setting.'))
    }
    setLoading(false)
    console.error(err)
  }

  return (
    <div className="properties-list">
      {loading ? (
        <Spinner size="medium" themeColor="dark-neutral" />
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <React.Fragment>
          {isAdding ? null : (
            <div className="property-readonly">
              <label>
                {`Account Num`}
                :&nbsp;
              </label>
              <div>{payorAccount.FrequentPayorNum}</div>
            </div>
          )}
          {isAdding || !payorAccount.CreatedInfo ? null : (
            <div className="property-readonly">
              <label>
                {`Created By`}
                :&nbsp;
              </label>
              <div>{payorAccount.CreatedInfo}</div>
            </div>
          )}
          {isAdding || !payorAccount.LastModifiedInfo ? null : (
            <div className="property-readonly">
              <label>
                {`Last Modified By`}
                :&nbsp;
              </label>
              <div>{payorAccount.LastModifiedInfo}</div>
            </div>
          )}
          <TextInput
            label={
                  <span>
                    Name<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
            value={payorAccount.FrequentPayorName}
            onChange={(e) => updatePayorValue('FrequentPayorName', e.target.value)}            
          />
          <Checkbox
            label="Is Active"
            checked={payorAccount.IsActive}
            onClick={(e) => updatePayorValue('IsActive', e.target.checked)}
          />
          {showBillableFlag ? (
            <Checkbox
              label="Is Account Billable (can have negative balance)"
              checked={payorAccount.IsBillableAccount}
              onClick={(e) => updatePayorValue('IsBillableAccount', e.target.checked)}
            />
          ) : null}
          {isAdding || !payorAccount.Balance ? null : (
            <div className="property-readonly">
              <label>
                {`Account Balance`}
                :&nbsp;
              </label>
              <div>${payorAccount.Balance.toFixed(2)}</div>
            </div>
          )}
          <TextInput            
            label={
                  <span>
                    {isUSA ? `Address` : `Address 1`}<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
            value={ContactAddress.AddressLine1}
            onChange={(e) => updatePayorAddressValue('AddressLine1', e.target.value)}
          />
          <TextInput
            label={isUSA ? `STE/APT/FL` : `Address 2`}
            value={ContactAddress.AddressLine2}
            onChange={(e) => updatePayorAddressValue('AddressLine2', e.target.value)}
          />
          <TextInput
            label={isUSA ? `Attention` : `Address 3`}
            value={ContactAddress.AddressLine3}
            onChange={(e) => updatePayorAddressValue('AddressLine3', e.target.value)}
          />
          {isUSA && (
            <div className="city-state-zip">
              <TextInput
                label={
                  <span>
                    City<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
                value={ContactAddress.City}
                onChange={(e) => updatePayorAddressValue('City', e.target.value)}
              />
              <SelectBox
                label={
                  <span>
                    State<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
                options={getStateOptions()}
                value={ContactAddress.StateOrProvince}
                onChange={(e) => updatePayorAddressValue('StateOrProvince', e)}
              />
              <TextInput
                label={
                  <span>
                    Zip<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
                value={ContactAddress.PostalCode}
                onChange={(e) => updatePayorAddressValue('PostalCode', e.target.value)}
              />
            </div>
          )}
          <SelectBox
            label={
                  <span>
                    Country<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
            options={getCountryOptions()}
            value={ContactAddress.Country}
            onChange={(e) => updatePayorAddressValue('Country', e)}
          />
          <TextInput
            label="Contact Email"
            value={payorAccount.ContactEmail}
            onChange={(e) => updatePayorValue('ContactEmail', e.target.value)}
          />
          <TextInput
            label="Contact Phone"
            value={payorAccount.ContactPhone}
            onChange={(e) => updatePayorValue('ContactPhone', e.target.value)}
          />
          <TextInput
            label={
                  <span>
                    Contact Name<span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                  </span>
                }
            value={payorAccount.ContactName}
            onChange={(e) => updatePayorValue('ContactName', e.target.value)}
          />
          <LinkedUserAccounts
            updatePayorValue={updatePayorValue}
            linkedAccounts={payorAccount.LinkedUserAccounts}
            checkConnected={true}
          />
          <div className="button-row">
            <ContextButton
              variant={'secondary'}
              onClick={() => {
                history.goBack()
              }}
            >
              {'Close'}
            </ContextButton>
            <ContextButton
              variant={'danger'}
              leftIcon={
                <div className="checkmark-wrapper left">
                  <Revert />
                </div>
              }
              onClick={resetPayorAccount}
              disabled={!isDirty}
            >
              {'Revert'}
            </ContextButton>
            <ContextButton
              rightIcon={
                <div className="checkmark-wrapper right">
                  <RightArrow />
                </div>
              }
              onClick={handleSave}
              disabled={!isDirty}
            >
              {isAdding ? 'Add' : 'Save'}
            </ContextButton>
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

export default PayorAccountDetails

const defaultPayorAccount = {
  FrequentPayorName: '',
  IsActive: true,
  IsBillableAccount: false,
  ContactEmail: '',
  ContactPhone: '',
  ContactName: '',
  LinkedUserAccounts: [],
  ContactAddress: {
    AddressLine1: '',
    AddressLine2: '',
    AddressLine3: '',
    City: '',
    StateOrProvince: '',
    PostalCode: '',
    Country: getUSADefault(),
  },
}
